import * as React from "react";
import { Settings, User, Bell, Shield, Palette, Globe, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isMultiLocale, SUPPORTED_LOCALES, LOCALE_META } from "../../../translations/config";

/**
 * SettingsPage - Settings categories
 *
 * Features:
 * - Settings category cards
 * - Consistent icon and typography styling
 * - Language preference section (only visible when isMultiLocale is true)
 * - i18n: text resolved via useTranslation('dashboard')
 */
export function SettingsPage() {
  const { t, i18n } = useTranslation("dashboard");

  const settingsCategories = [
    {
      icon: User,
      label: "Profile",
      description: "Manage your personal information",
      color: "text-primary",
    },
    {
      icon: Bell,
      label: "Notifications",
      description: "Configure notification preferences",
      color: "text-amber-600",
    },
    {
      icon: Shield,
      label: "Security",
      description: "Password and authentication",
      color: "text-emerald-600",
    },
    {
      icon: Palette,
      label: "Appearance",
      description: "Theme and display settings",
      color: "text-purple-600",
    },
    {
      icon: Globe,
      label: "Language & Region",
      description: "Localization preferences",
      color: "text-blue-600",
    },
    {
      icon: Settings,
      label: "Advanced",
      description: "Developer and advanced options",
      color: "text-muted-foreground",
    },
  ];

  const handleLanguageChange = (locale: string) => {
    i18n.changeLanguage(locale);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          {t("pages.settings.title")}
        </h1>
        <p className="text-base text-muted-foreground md:text-lg">
          {t("pages.settings.description")}
        </p>
      </div>

      <Separator />

      {/* Language preference - only shown when multiple locales are supported */}
      {isMultiLocale && (
        <section className="space-y-4">
          <h2 className="text-xs font-medium tracking-widest text-muted-foreground uppercase">
            {t("pages.settings.language.title")}
          </h2>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h3 className="font-medium text-foreground">
                    {t("pages.settings.language.title")}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t("pages.settings.language.description")}
                  </p>
                </div>
                <Select value={i18n.language} onValueChange={handleLanguageChange}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_LOCALES.map((locale) => {
                      const meta = LOCALE_META[locale];
                      return (
                        <SelectItem key={locale} value={locale}>
                          <span className="flex items-center gap-2">
                            <span>{meta?.flag}</span>
                            <span>{meta?.name ?? locale}</span>
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Settings categories */}
      <section className="space-y-4">
        <h2 className="text-xs font-medium tracking-widest text-muted-foreground uppercase">
          Settings Categories
        </h2>

        <div className="grid gap-3">
          {settingsCategories.map((category) => {
            const Icon = category.icon;
            return (
              <Card
                key={category.label}
                className="group cursor-pointer transition-colors hover:bg-muted/50"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-muted transition-colors group-hover:border-border">
                      <Icon className={`h-5 w-5 ${category.color}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-foreground">{category.label}</h3>
                      <p className="truncate text-sm text-muted-foreground">
                        {category.description}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground/50 transition-colors group-hover:text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  );
}

export default SettingsPage;
